import {
    authenticatedApi,
    ApiError,
    API_BASE_URL
} from './apiUtils';

export const radarApi = {
  getHierarchy: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/segments/hierarchy`);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to fetch radar hierarchy', 500);
    }
  },

  getFirstLevelHierarchy: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/segments/hierarchy/firstlevel`);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to fetch first level radar hierarchy', 500);
    }
  },

  updateSegment: async (segmentId, updateData) => {
    const { name, description, segmentType, color, tags, projects, approvalStage, processes } = updateData;

    // Clean up processes: remove client-side tempId field
    const cleanProcesses = processes?.map(({ tempId, ...rest }) => rest) || [];

    const payload = {
      name,
      description,
      segmentType,
      ...(segmentType === 'Internal'
        ? { color, segmentProcesses: cleanProcesses }
        : { tags, projects, approvalLevel: approvalStage })
    };

    try {
      return await authenticatedApi.put(`${API_BASE_URL}/segments/${segmentId}`, payload);
    } catch (error) {
      throw new ApiError('Failed to update segment', error.status || 500);
    }
  },

  addSegment: async (segmentData) => {
    const { name, parentSegmentId, description, segmentType, color, tags, projects, approvalStage, processes } = segmentData;

    // Clean up processes: remove client-side tempId field
    const cleanProcesses = processes?.map(({ tempId, ...rest }) => rest) || [];

    const payload = {
      name,
      parentSegmentId,
      description,
      segmentType,
      ...(segmentType === 'Internal'
        ? { color, segmentProcesses: cleanProcesses }
        : { tags, projects, approvalLevel: approvalStage })
    };

    try {
      return await authenticatedApi.post(`${API_BASE_URL}/segments`, payload);
    } catch (error) {
      throw new ApiError('Failed to create segment', error.status || 500);
    }
  },

  removeSegment: async (segmentId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/segments/${segmentId}`);
    } catch (error) {
      throw new ApiError('Failed to delete segment', error.status || 500);
    }
  },

  getSegmentUsage: async (segmentId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/segments/${segmentId}/usage`);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to fetch segment usage', error.status || 500);
    }
  },

  safeDeleteSegment: async (segmentId, { action, replacementLeafSegmentId, confirmation } = {}) => {
    try {
      const body = {};
      if (action) {
        body.action = action;
      }
      if (replacementLeafSegmentId) {
        body.replacementLeafSegmentId = replacementLeafSegmentId;
      }
      if (confirmation) {
        body.confirmation = confirmation;
      }
      return await authenticatedApi.delete(`${API_BASE_URL}/segments/${segmentId}`, body);
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError('Failed to delete segment', error.status || 500);
    }
  },

  moveSegment: async (segmentId, destinationParentId) => {
    try {
      return await authenticatedApi.patch(`${API_BASE_URL}/segments/${segmentId}/move`, {
        destinationParentId
      });
    } catch (error) {
      throw new ApiError('Failed to move segment', error.status || 500);
    }
  },

  getProjectHierarchy: async (projectId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/segments/hierarchy/project/${projectId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch project hierarchy', error.status || 500);
    }
  },

  getDashboardHierarchy: async (dashboardId) => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/segments/hierarchy/dashboard/${dashboardId}`);
    } catch (error) {
      throw new ApiError('Failed to fetch dashboard hierarchy', error.status || 500);
    }
  },

  getCustomSegments: async () => {
    try {
      return await authenticatedApi.get(`${API_BASE_URL}/custom-segments`);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to fetch custom segments', 500);
    }
  },

  reconcileCustomSegment: async (customSegmentId, leafSegmentId) => {
    try {
      return await authenticatedApi.post(`${API_BASE_URL}/custom-segments/${customSegmentId}/reconcile`, {
        leafSegmentId
      });
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to reconcile custom segment', error.status || 500);
    }
  },

  deleteCustomSegment: async (customSegmentId) => {
    try {
      return await authenticatedApi.delete(`${API_BASE_URL}/custom-segments/${customSegmentId}`);
    } catch (error) {
      if (error instanceof ApiError) throw error;
      throw new ApiError('Failed to delete custom segment', error.status || 500);
    }
  },
};